import React, { useState, useEffect } from 'react';
import { 
  X, 
  Plus, 
  Trash,
  PaperPlaneRight,
  MagnifyingGlass,
  FloppyDisk,
  ChatText
} from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SavedTextsPanel = ({ 
  onTextSelect, 
  onClose, 
  userId,
  userRole = 'handler' // Only for handler/admin
}) => {
  const [savedTexts, setSavedTexts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newText, setNewText] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSavedTexts();
  }, [userId]);

  const loadSavedTexts = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/saved-texts?user_id=${userId}`);
      setSavedTexts(response.data.saved_texts || []);
    } catch (error) {
      console.error('Failed to load saved texts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddText = async () => {
    if (!newText.trim()) return;

    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/saved-texts`, {
        user_id: userId,
        text: newText.trim(),
        title: newTitle.trim() || '',
        is_global: false
      });

      if (response.data.success) {
        setSavedTexts([...savedTexts, response.data.saved_text]);
        setNewText('');
        setNewTitle('');
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Failed to save text:', error);
      alert('Failed to save text: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteText = async (textId) => {
    if (!window.confirm('Delete this saved text?')) return;

    try {
      await axios.delete(`${API}/saved-texts/${textId}?user_id=${userId}`);
      setSavedTexts(savedTexts.filter(t => t.id !== textId));
    } catch (error) {
      console.error('Failed to delete text:', error);
      alert('Failed to delete text');
    }
  };

  const handleSendText = (text) => {
    onTextSelect(text);
  };

  // Filter texts based on search
  const filteredTexts = searchQuery 
    ? savedTexts.filter(t => t.text.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
      <div className="bg-white w-full max-w-md rounded-t-2xl max-h-[70vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <FloppyDisk size={24} className="text-green-600" />
            Quick Replies
          </h3>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowAddForm(true)}
              className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600"
            >
              <Plus size={20} />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search saved texts..."
              className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="p-4 border-b bg-green-50">
            <div className="space-y-3">
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Title (optional)"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Enter text or link to save..."
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                autoFocus
              />

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewText('');
                    setNewTitle('');
                  }}
                  className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddText}
                  disabled={!newText.trim() || isSaving}
                  className="flex-1 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <FloppyDisk size={18} />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Search Results */}
              {searchQuery && filteredTexts && (
                <div className="space-y-2">
                  {filteredTexts.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No results found</p>
                  ) : (
                    filteredTexts.map(item => (
                      <SavedTextItem 
                        key={item.id}
                        item={item}
                        onSend={handleSendText}
                        onDelete={handleDeleteText}
                      />
                    ))
                  )}
                </div>
              )}

              {/* All Saved Texts */}
              {!searchQuery && (
                <>
                  {savedTexts.length === 0 ? (
                    <div className="text-center py-12">
                      <ChatText size={48} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No saved texts yet</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Click + to add your first quick reply
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {savedTexts.map(item => (
                        <SavedTextItem 
                          key={item.id}
                          item={item}
                          onSend={handleSendText}
                          onDelete={handleDeleteText}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Individual saved text item
const SavedTextItem = ({ item, onSend, onDelete }) => {
  // Check if text is a URL
  const isUrl = item.text.startsWith('http://') || item.text.startsWith('https://');
  
  return (
    <div className="bg-gray-50 rounded-lg p-3 group hover:bg-gray-100 transition-colors">
      {item.title && (
        <p className="text-xs font-semibold text-green-600 mb-1">{item.title}</p>
      )}
      <div className="flex items-start justify-between gap-2">
        <p className={`flex-1 text-sm text-gray-800 whitespace-pre-wrap break-all ${isUrl ? 'text-blue-600 underline' : ''}`}>
          {item.text}
        </p>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onSend(item.text)}
            className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600"
            title="Send"
          >
            <PaperPlaneRight size={16} weight="fill" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-2 text-red-500 hover:bg-red-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SavedTextsPanel;
